import { CITY_DISTRICTS } from '@/data/cityDistricts';
import { SUPPORTED_CITIES } from '@/lib/geoScope';
import { WARSAW_MSI_REJONY } from '@/data/warsawMsiRejony';
import { WARSAW_METRO } from '@/data/warsawMetro';

/** Proximity radius for a metro-station chip (~10 min walk). Filter is a square
 * bbox of ±this — inclusive of the true circle, a few corner false-positives. */
export const STATION_RADIUS_M = 800;


/**
 * SERP area search — fully LOCAL (no geocoding API on this public
 * per-keystroke hot path). Districts (18 official) filter by name; osiedla =
 * official MSI rejony (143) filter by NAME on extracted_subdistrict, so the
 * picker names are exactly the rejon we label on each offer (one system, no
 * OSM↔MSI rozjazd). Only offers with a detected MSI rejon match.
 */

// `SearchArea` mieszka od F3 w `src/lib/serpRead/types.ts` (ścieżka odczytu wspólna z funkcją
// `send-filter-alerts`, plan §1.68); stąd eksport dalej dla dotychczasowych importów.
export type { SearchArea } from '@/lib/serpRead/types';
import type { SearchArea } from '@/lib/serpRead/types';
import { PLACE_RADIUS_M } from '@/lib/serpRead/miejsce';
export { PLACE_RADIUS_M, normalizujMiejsca, zJednymMiejscem } from '@/lib/serpRead/miejsce';

export interface AreaSuggestion {
  area: SearchArea;
  type: 'city' | 'district' | 'estate' | 'station';
  /** Parent-district context for estates ("Muranów · Śródmieście"). */
  sublabel: string | null;
  /** Future cities: visible ("wkrótce") but not selectable until their scrape
   * coverage lands — the query clause already supports them. */
  disabled?: boolean;
}

/** Lowercase + strip Polish diacritics so "zoliborz" matches Żoliborz. */
export function foldPolish(s: string): string {
  return s
    .toLowerCase()
    .replace(/ł/g, 'l')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

const WARSAW_DISTRICTS = CITY_DISTRICTS['Warszawa'].districts;

// Supported (not-yet-live) cities become {kind:'city'} chips (extracted_city
// match), shown "wkrótce"/disabled. The active city (Warszawa) is the default
// scope, not a chip. Source is the one geo gate (geoScope.SUPPORTED_CITIES),
// not a local Warszawa filter — active↔supported stays in lockstep app-wide.
// District-level chips for these cities come later, when their scrape coverage
// lands — CITY_DISTRICTS already has their districts and the clause builder
// scopes districts per city, so it's an areaSearch-only change.
const OTHER_CITIES = SUPPORTED_CITIES;

function rank(foldedName: string, foldedQuery: string): number {
  // startsWith beats substring; shorter names beat longer ("Muranów" before
  // "Muranów Południowy").
  if (foldedName.startsWith(foldedQuery)) return foldedName.length;
  return 1000 + foldedName.length;
}

export function searchAreas(query: string, limit = 8): AreaSuggestion[] {
  const q = foldPolish(query.trim());
  if (q.length < 2) return [];

  const cities = OTHER_CITIES
    .filter((name) => foldPolish(name).includes(q))
    .map((name): AreaSuggestion => ({
      area: { kind: 'city', name },
      type: 'city',
      sublabel: null,
      disabled: true,
    }));

  const districts = WARSAW_DISTRICTS
    .map((d) => ({ d, folded: foldPolish(d.name) }))
    .filter(({ folded }) => folded.includes(q))
    .sort((a, b) => rank(a.folded, q) - rank(b.folded, q))
    .map(({ d }): AreaSuggestion => ({
      area: { kind: 'district', name: d.name },
      type: 'district',
      sublabel: null,
    }));

  // Metro stations (M1 + M2). Selecting one filters offers within STATION_RADIUS_M
  // of the station (≈10 min walk) — a bbox over geocoded coords.
  const stations = WARSAW_METRO
    .map((s) => ({ s, folded: foldPolish(s.name) }))
    .filter(({ folded }) => folded.includes(q))
    .sort((a, b) => rank(a.folded, q) - rank(b.folded, q))
    .map(({ s }): AreaSuggestion => ({
      area: { kind: 'station', name: s.name, lat: s.lat, lon: s.lon, radiusM: STATION_RADIUS_M },
      type: 'station',
      sublabel: null,
    }));

  // Osiedla = official MSI rejony. Parent district comes straight from MSI (no
  // centroid guessing). Selecting one filters by extracted_subdistrict == name.
  const rejony = WARSAW_MSI_REJONY
    .map((r) => ({ r, folded: foldPolish(r.name) }))
    .filter(({ folded }) => folded.includes(q))
    .sort((a, b) => rank(a.folded, q) - rank(b.folded, q))
    .map(({ r }): AreaSuggestion => ({
      area: { kind: 'subdistrict', name: r.name },
      type: 'estate',
      sublabel: r.district,
    }));

  // Cities (rare) → metro stations (strong intent, few) → districts → osiedla.
  return [...cities, ...stations, ...districts, ...rejony].slice(0, limit);
}

/** Stable identity for chips/serialization de-dupe. */
export function areaKey(a: SearchArea): string {
  if (a.kind === 'city') return `c:${a.name}`;
  if (a.kind === 'district') return `d:${a.name}`;
  if (a.kind === 'subdistrict') return `s:${a.name}`;
  if (a.kind === 'station') return `m:${a.name}`;
  if (a.kind === 'place') return `p:${a.label}@${a.lat},${a.lon}`;
  return `g:${a.label}@${a.lat},${a.lon}`;
}

/** The area's own name: `geo` and `place` carry `label`, the rest `name`. */
export function nazwaObszaru(a: SearchArea): string {
  return a.kind === 'geo' || a.kind === 'place' ? a.label : a.name;
}

/** What a chip or summary shows: a place reads „Blisko: <name>” (plan E4.2), the rest its name. */
export function etykietaObszaru(a: SearchArea, t: (key: string, opts?: Record<string, unknown>) => string): string {
  return a.kind === 'place' ? t('search.area.place_chip', { name: a.label }) : nazwaObszaru(a);
}


/* ---------- URL serialization (used by src/lib/serpUrl.ts) ---------- */

const num = (v: string) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * Escapes only what separates fields here: `%` (so decoding is unambiguous), `|` (between areas)
 * and `~` (between a name and its coordinates). Everything else, Polish letters included, stays as
 * is, because URLSearchParams encodes the value once. Encoding the whole name too made the URL
 * double-encoded (`Mokot%25C3%25B3w`), unreadable after one decode (stage E2E, 15.10).
 * `parseArea` decodes with decodeURIComponent, so old fully encoded links still parse.
 */
const SEPARATORY: Record<string, string> = { '%': '%25', '|': '%7C', '~': '%7E' };
function zakodujNazwe(s: string): string {
  return s.replace(/[%|~]/g, (c) => SEPARATORY[c]);
}

/** `c:Wrocław` | `d:Wola` | `m:Szwedzka~lat,lon~radiusM` | `p:<label>~lat,lon~radiusM` | `g:<label>~lat,lon~west,south,east,north` (separators escaped, `zakodujNazwe`). */
export function serializeArea(a: SearchArea): string {
  if (a.kind === 'city') return `c:${zakodujNazwe(a.name)}`;
  if (a.kind === 'district') return `d:${zakodujNazwe(a.name)}`;
  if (a.kind === 'subdistrict') return `s:${zakodujNazwe(a.name)}`;
  if (a.kind === 'station') return `m:${zakodujNazwe(a.name)}~${a.lat},${a.lon}~${a.radiusM}`;
  if (a.kind === 'place') return `p:${zakodujNazwe(a.label)}~${a.lat},${a.lon}~${a.radiusM}`;
  return `g:${zakodujNazwe(a.label)}~${a.lat},${a.lon}~${a.west},${a.south},${a.east},${a.north}`;
}

export function parseArea(s: string): SearchArea | null {
  if (s.startsWith('c:')) {
    const name = decodeURIComponent(s.slice(2));
    return name ? { kind: 'city', name } : null;
  }
  if (s.startsWith('d:')) {
    const name = decodeURIComponent(s.slice(2));
    return name ? { kind: 'district', name } : null;
  }
  if (s.startsWith('s:')) {
    const name = decodeURIComponent(s.slice(2));
    return name ? { kind: 'subdistrict', name } : null;
  }
  if (s.startsWith('m:') || s.startsWith('p:')) {
    const [namePart, centerPart, radiusPart] = s.slice(2).split('~');
    if (!namePart || !centerPart || !radiusPart) return null;
    const [lat, lon] = centerPart.split(',').map(num);
    const radiusM = num(radiusPart);
    if ([lat, lon, radiusM].some((n) => n == null)) return null;
    const name = decodeURIComponent(namePart);
    if (s.startsWith('m:')) return { kind: 'station', name, lat: lat!, lon: lon!, radiusM: radiusM! };
    // A hand-edited link cannot widen the place: the radius is fixed (plan §1.18), out-of-range
    // coordinates make no area.
    if (lat! < -90 || lat! > 90 || lon! < -180 || lon! > 180) return null;
    return { kind: 'place', label: name, lat: lat!, lon: lon!, radiusM: PLACE_RADIUS_M };
  }
  if (!s.startsWith('g:')) return null;
  const [labelPart, centerPart, bboxPart] = s.slice(2).split('~');
  if (!labelPart || !centerPart || !bboxPart) return null;
  const [lat, lon] = centerPart.split(',').map(num);
  const [west, south, east, north] = bboxPart.split(',').map(num);
  if ([lat, lon, west, south, east, north].some((n) => n == null)) return null;
  return {
    kind: 'geo',
    label: decodeURIComponent(labelPart),
    lat: lat!,
    lon: lon!,
    west: west!,
    south: south!,
    east: east!,
    north: north!,
  };
}
